import { Injectable } from '@angular/core'

import { Observable, catchError, map, throwError } from 'rxjs'

import {
  GetTrygroupPersonalAnalysisGQL,
  GetTrygroupPersonalAnalysisQuery,
  TrygroupFactorAnalysisStatus,
} from '../../../_generated/graphql'
import { throwIfCustomError } from '../../util/throwIfCustomError'
import { assertGraphqlData } from '../../util/assertGraphqlData'

type GraphqlTrygroupPersonalAnalysis =
  GetTrygroupPersonalAnalysisQuery['trygroupPersonalAnalysis']

type GraphqlTrygroupFactorAnalysis =
  GraphqlTrygroupPersonalAnalysis['factorAnalyses'][number]

type GraphqlTrygroupPatternSummary =
  GraphqlTrygroupFactorAnalysis['patternSummaries'][number]

export type TrygroupPatternType = 'ng_talk' | 'missing'

export type TrygroupPatternSummary = Omit<
  GraphqlTrygroupPatternSummary,
  'patternType'
> & {
  patternType: TrygroupPatternType | null
}

export type TrygroupFactorAnalysis = Omit<
  GraphqlTrygroupFactorAnalysis,
  'patternSummaries'
> & {
  patternSummaries: TrygroupPatternSummary[]
}

export type TrygroupPersonalAnalysis = Omit<
  GraphqlTrygroupPersonalAnalysis,
  'factorAnalyses'
> & {
  factorAnalyses: TrygroupFactorAnalysis[]
}

export { TrygroupFactorAnalysisStatus }

export type ProjectProgress = NonNullable<
  TrygroupPersonalAnalysis['projectProgress']
>

@Injectable({
  providedIn: 'root',
})
export class TrygroupPersonalAnalysisService {
  constructor(
    private getTrygroupPersonalAnalysisGQL: GetTrygroupPersonalAnalysisGQL,
  ) {}

  getPersonalAnalysis(userId: string): Observable<TrygroupPersonalAnalysis> {
    return this.getTrygroupPersonalAnalysisGQL
      .fetch({ variables: { userId }, fetchPolicy: 'no-cache' })
      .pipe(
        map((result) => {
          assertGraphqlData(result)
          return mapPersonalAnalysis(result.data.trygroupPersonalAnalysis)
        }),
        catchError((error: unknown) => {
          throwIfCustomError(error)
          return throwError(() => error)
        }),
      )
  }
}

/** GraphQL レスポンスを patternType の値域を保証したビューモデルへ変換する。 */
function mapPersonalAnalysis(
  analysis: GraphqlTrygroupPersonalAnalysis,
): TrygroupPersonalAnalysis {
  return {
    ...analysis,
    factorAnalyses: analysis.factorAnalyses.map((factorAnalysis) => ({
      ...factorAnalysis,
      patternSummaries: factorAnalysis.patternSummaries.map(
        (patternSummary) => ({
          ...patternSummary,
          patternType: parsePatternType(patternSummary.patternType),
        }),
      ),
    })),
  }
}

/** GraphQL の文字列を許可済みのパターン種別へ変換する。 */
function parsePatternType(
  patternType: string | null | undefined,
): TrygroupPatternType | null {
  if (typeof patternType === 'undefined') {
    throw new Error('Unsupported patternType: undefined')
  }

  if (patternType == null) {
    return null
  }

  if (patternType === 'ng_talk' || patternType === 'missing') {
    return patternType
  }

  throw new Error(`Unsupported patternType: ${String(patternType)}`)
}
